import React, { useState } from 'react';
import { useActions } from '../../../Actions';
import useEffectCallback from '../../../util/useEffectCallback';
import { useAccountId } from '../../../auth/useAccountId';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { State } from '../../../reducer';
import namespace from '../namespace';
import Page, { Back } from '../../../components/Page';
import Card from '../../../components/Card';
import { H1, H2 } from '../../../components/Typography';
import { Dd, Dl, Dt } from '../../../components/DescriptionList/Horizontal';
import { Money } from '../../../components/Formatters';
import OrderItems from '../../components/OrderItems';
import CustomerInfo from '../../components/CustomerInfo';
import OrderShippingAddress from '../../components/OrderShippingAddress';
import OrderBillingAddress from '../../components/OrderBillingAddress';
import DeviceInfo from '../../components/DeviceInfo';
import SessionHistory from './SessionHistory';
import { ButtonGroup, ButtonLink } from '../../../components/Buttons';
import styled from 'styled-components/macro';
import { border, colors, distances, globalColumnMaxWidth } from '../../../styles/constants';
import { useTranslation } from 'react-i18next';
import { getLastSessionEventName, isSessionExpired, SESSION_END_STATES } from '../../sessionHelpers';
import { CaptureStates } from '../actions';
import { CHECKOUT_API_HOSTNAME } from '../../../env';
import { Session } from '../../../types/checkout';
import { useInterval } from '../../../common/hooks/useInterval';
import Hr from '../../../components/Hr';
const POLL_INTERVAL_MS = 2000;

const hostedCheckoutUrl = (session: Session | undefined): string => {
    if (!session) {
        return '';
    }

    if (session.configuration.netaxept) {
        return `${CHECKOUT_API_HOSTNAME}/v1/view/${session.id}/netaxept`;
    }
    return `${CHECKOUT_API_HOSTNAME}/v1/view/${session.id}`;
};

const shouldPoll = (session: Session | undefined) => {
    if (!session) {
        return false;
    }
    const lastEventName = getLastSessionEventName(session);
    const isEndState = SESSION_END_STATES.includes(lastEventName || '');
    const sessionExpired = isSessionExpired(session);
    return !(isEndState || sessionExpired);
};

const SessionDetails = () => {
    const accountId = useAccountId();
    const sessionId = useParams<{ sessionId: string }>().sessionId;
    const [copyStatus, setCopyStatus] = useState('initial');

    const { t } = useTranslation();

    const { fetchSession } = useActions('sessionDetails');

    useEffectCallback(fetchSession, [accountId, sessionId]);

    const { session, captureState } = useSelector<State, State[typeof namespace]>(
        (s) => s[namespace]
    );
    const weShouldPoll = shouldPoll(session);

    useInterval(() => {
        fetchSession(accountId, sessionId);
    }, weShouldPoll ? POLL_INTERVAL_MS : null);

    if (!session) {
        return null;
    }

    const sessionExpired = isSessionExpired(session);
    const lastEventName = getLastSessionEventName(session) || '';

    const publishTypes = ['checkout-link', 'app'];
    const publishChannels = ['sms', 'push'];
    const publish =
        session?.configuration.publish?.find(
            (elem) => publishTypes.includes(elem.type) && publishChannels.includes(elem.channel)
        );
    const auto_capture = session?.configuration.auto_capture ? 'enabled' : 'disabled';
    const checkoutUrl = hostedCheckoutUrl(session);

    const addToClipboard = async (text: string) => {
        setCopyStatus('initial');
        try {
            await navigator.clipboard.writeText(text);
            setCopyStatus('copy_success');
        } catch (err) {
            setCopyStatus('copy_failed');
        }
    };

    return (
        <Page
            title={
                <React.Fragment>
                    <Back />
                    <Title>{t('payments.session.title')}</Title>
                </React.Fragment>
            }
            hasEnvFrame={true}
        >
            <Card title={undefined}>
                <Wrapper>
                    <H2>
                        {captureState === CaptureStates.None && (
                            <>
                                {!sessionExpired &&
                                    ['INITIATED', 'VISITED', 'COMPLETED', 'PRELOADED'].includes(lastEventName) &&
                                    t('payments.virtual_terminal.poll_session.payment_in_progress')}
                                {!sessionExpired &&
                                    ['FAILED', 'DECLINED', 'UNKNOWN'].includes(lastEventName) &&
                                    t('payments.virtual_terminal.poll_session.payment_failed')}
                                {!sessionExpired &&
                                    'AUTHORIZED' === lastEventName &&
                                    t('payments.virtual_terminal.poll_session.payment_transaction_authorized')}
                                {sessionExpired &&
                                    t('payments.virtual_terminal.poll_session.checkout_session_expired')}
                            </>
                        )}
                        {captureState === CaptureStates.Captured &&
                            t('payments.virtual_terminal.poll_session.payment_captured')}
                        {captureState === CaptureStates.Failed &&
                            t('payments.virtual_terminal.poll_session.payment_capture_failed')}
                    </H2>
                    <Hr />
                    <Dl>
                        <Dt doubleColumn>{t('payments.virtual_terminal.fields.amount')}</Dt>
                        <Dd>
                            <Money amount={session.order.amount} currency={session.order.currency} />
                        </Dd>
                    </Dl>
                    <Dl>
                        <Dt doubleColumn>{t('payments.virtual_terminal.fields.description')}</Dt>
                        <Dd>
                            {session.order.items && session.order.items[0] && session.order.items[0].description}
                        </Dd>
                    </Dl>
                    <Dl>
                        <Dt doubleColumn>{t('payments.virtual_terminal.poll_session.fields.id')}</Dt>
                        <Dd>
                            <Code>{session.id}</Code>
                        </Dd>
                    </Dl>
                    {session.payment_operation && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.payment_operation')}</Dt>
                            <Dd>
                                {t(`payments.payment_operation.${session.payment_operation}`)}
                            </Dd>
                        </Dl>
                    )}
                    {session.configuration.channel && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.channel')}</Dt>
                            <Dd>
                                {t(`payments.channel.${session.configuration.channel}`)}
                            </Dd>
                        </Dl>
                    )}
                    <Dl>
                        <Dt doubleColumn>{t('payments.virtual_terminal.poll_session.fields.merchant_reference')}</Dt>
                        <Dd>
                            <Code>{session.order.merchant_reference}</Code>
                        </Dd>
                    </Dl>
                    {!session.transaction_id && ((publish && publish.channel === 'sms') || !publish) && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.virtual_terminal.poll_session.fields.url')}</Dt>
                            <Dd>
                                <Code>
                                    {checkoutUrl}
                                    <Tag onClick={() => addToClipboard(checkoutUrl)}>
                                        {t('payments.virtual_terminal.poll_session.copy', {
                                            context: copyStatus,
                                        })}
                                    </Tag>
                                </Code>
                            </Dd>
                        </Dl>
                    )}
                    {publish && publish.channel === 'sms' && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.virtual_terminal.poll_session.fields.sms_status')}</Dt>
                            <Dd>
                                {t(
                                    `payments.virtual_terminal.poll_session.publish_statuses.${
                                        publish ? (publish.status as 'failed' | 'missing' | 'sent' | 'skipped') : 'missing'
                                    }`
                                )}
                            </Dd>
                        </Dl>
                    )}
                    {publish && publish.channel === 'push' && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.virtual_terminal.poll_session.fields.push_status')}</Dt>
                            <Dd>
                                {t(
                                    `payments.virtual_terminal.poll_session.publish_statuses.${
                                        publish ? (publish.status as 'failed' | 'missing' | 'sent' | 'skipped'): 'missing'
                                    }`
                                )}
                            </Dd>
                        </Dl>
                    )}
                    <Dl>
                        <Dt doubleColumn>{t('payments.virtual_terminal.poll_session.fields.auto_capture')}</Dt>
                        <Dd>{t(`payments.virtual_terminal.poll_session.auto_capture.${auto_capture}`)}</Dd>
                    </Dl>

                    <OrderItems session={session} />

                    <CustomerInfo session={session} />

                    <SectionsFlexWrapper>
                        <OrderShippingAddress session={session} />
                        <OrderBillingAddress session={session} />
                    </SectionsFlexWrapper>

                    <DeviceInfo session={session} />

                    <SessionHistory session={session} sessionExpired={sessionExpired} captureState={captureState} />

                    <ButtonGroup>
                        {session && session.transaction_id && <ButtonLink
                            to={
                                `/${accountId}/payments/transactions/${session.transaction_id}`
                            }
                        >
                            {t('payments.virtual_terminal.poll_session.call_to_action.open_transaction')}
                        </ButtonLink>}
                        <ButtonLink to={`/${accountId}/payments/virtual-terminal`}>
                            {t('payments.virtual_terminal.poll_session.call_to_action.create_new_payment')}
                        </ButtonLink>
                        <ButtonLink to={{
                            pathname: `/${accountId}/payments/virtual-terminal`,
                            search: `copySession=${session.id}`,
                        }}>
                            {t('payments.operations.copy_new_payment_link')}
                        </ButtonLink>
                    </ButtonGroup>
                </Wrapper>
            </Card>
        </Page>
    );
};

const Title = styled(H1)`
    display: inline-block;
        /* Heading/H6/Medium/Desktop */
        font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 28px; /* 140% */
    letter-spacing: -0.4px;
`;

const Wrapper = styled.div`
    position: relative;
    min-height: calc(${distances.ginormous} + ${distances.large});
`;

const Code = styled.code`
    white-space: normal;
`;

const SectionsFlexWrapper = styled.div`
  display: flex;

  @media (max-width: ${globalColumnMaxWidth}px) {
    flex-direction: column;
  }
`;

const Tag = styled.span`
    background-color: ${colors.primary};
    font-size: 12px;
    margin-left: 5px;
    border-radius: 2px;
    padding: 1px 5px;
    color: #fff;

    &:hover {
        cursor: pointer;
        background-color: ${colors.primaryHover};
    }
`;



export default SessionDetails;
